import { ReactNode } from 'react';
import { Box } from '@amzn/awsui-components-react';
import { ActivitySummaryPanel } from '../activitySummary/activitySummaryPanel';
import { DualValueTile, SingleValueTile, TrackingRateTile } from '../activitySummary/summaryTiles';
import {
  formatCount,
  formatHours,
  formatYearInTimeZone,
  getDualValueDisplay,
  getTrackingRatePercent,
  isMetricAvailable,
  minutesToHours,
} from '../activitySummary/displayUtils';
import { ActivitySummaryMetric, VolunteerActivitySummary } from '@amzn/red-velvet-api';
import { useVolunteerActivitySummary } from '../../hooks/activitySummary/useVolunteerActivitySummary';
import { useLocalizedText } from '../../hooks/localizedText';
import { useLocale } from '../../hooks/locale';
import { useTimezone } from '../../hooks/timezone';

/**
 * The Volunteer summary on /my-activity (cce-4530). Renders nothing while loading, on a failed
 * request, or when the API leaves out `summary`, so the tabs below always load as before.
 */
export function VolunteerActivitySummaryContainer() {
  const { data, isSuccess } = useVolunteerActivitySummary();
  if (!isSuccess || !data.summary) return null;
  return <VolunteerActivitySummaryView summary={data.summary} />;
}

/** Spacing lives here, not in the page, so a hidden summary leaves no gap above the tabs. */
function SummarySpacing({ children }: { children: ReactNode }) {
  return <Box margin={{ bottom: 'l' }}>{children}</Box>;
}

export function VolunteerActivitySummaryView({ summary }: { summary: VolunteerActivitySummary }) {
  const { t } = useLocalizedText(['activity']);
  const locale = useLocale();
  const timeZone = useTimezone();
  const { year, unavailableMetrics } = summary;

  const volunteeringSince = isMetricAvailable(
    summary.volunteeringSince,
    ActivitySummaryMetric.VOLUNTEERING_SINCE,
    unavailableMetrics
  )
    ? summary.volunteeringSince
    : undefined;
  const shiftsCompleted = isMetricAvailable(
    summary.shiftsCompleted,
    ActivitySummaryMetric.SHIFTS_COMPLETED,
    unavailableMetrics
  )
    ? summary.shiftsCompleted
    : undefined;
  const volunteerMinutes = isMetricAvailable(
    summary.volunteerMinutes,
    ActivitySummaryMetric.VOLUNTEER_MINUTES,
    unavailableMetrics
  )
    ? summary.volunteerMinutes
    : undefined;
  const trackingPercent = isMetricAvailable(
    summary.attendanceTracking,
    ActivitySummaryMetric.ATTENDANCE_TRACKING,
    unavailableMetrics
  )
    ? getTrackingRatePercent(summary.attendanceTracking)
    : undefined;

  const formatCountValue = (value: number) => formatCount(value, locale);
  const formatMinutesAsHours = (minutes: number) => formatHours(minutesToHours(minutes), locale);

  const tiles: ReactNode[] = [];
  if (volunteeringSince) {
    tiles.push(
      <SingleValueTile
        key="volunteeringSince"
        label={t('activitySummary.volunteer.volunteeringSince')}
        value={formatYearInTimeZone(volunteeringSince, timeZone, locale)}
      />
    );
  }
  if (shiftsCompleted) {
    tiles.push(
      <DualValueTile
        key="shiftsCompleted"
        label={t('activitySummary.volunteer.shiftsCompleted')}
        display={getDualValueDisplay(shiftsCompleted, year)}
        formatValue={formatCountValue}
      />
    );
  }
  if (volunteerMinutes) {
    tiles.push(
      <DualValueTile
        key="volunteerHours"
        label={t('activitySummary.volunteer.volunteerHours')}
        display={getDualValueDisplay(volunteerMinutes, year)}
        formatValue={formatMinutesAsHours}
      />
    );
  }
  if (trackingPercent !== undefined) {
    tiles.push(
      <TrackingRateTile
        key="attendanceTracking"
        label={t('activitySummary.volunteer.attendanceTrackingRate')}
        percent={trackingPercent}
        infoContent={t('activitySummary.volunteer.attendanceTrackingInfo')}
      />
    );
  }

  // The lead sentence reuses the tiles' this-year values, so it hides when either failed to load
  // or when both are 0 (OQ-10).
  let leadSentence: string | undefined;
  if (
    shiftsCompleted &&
    volunteerMinutes &&
    (shiftsCompleted.thisYear > 0 || volunteerMinutes.thisYear > 0)
  ) {
    // Plural form follows the displayed (one-decimal) value, so 0.95 hours reads "1 hour".
    const hours = Math.round(minutesToHours(volunteerMinutes.thisYear) * 10) / 10;
    leadSentence = t('activitySummary.volunteer.leadSentence', {
      hours: t('activitySummary.volunteer.leadHours', {
        count: hours,
        value: formatHours(hours, locale),
      }),
      shifts: t('activitySummary.volunteer.leadShifts', {
        count: shiftsCompleted.thisYear,
        value: formatCount(shiftsCompleted.thisYear, locale),
      }),
    });
  }

  // Every tile hidden or failed hides the whole container.
  if (tiles.length === 0) return null;

  return (
    <SummarySpacing>
      <ActivitySummaryPanel
        header={t('activitySummary.volunteer.header')}
        leadSentence={leadSentence}
        tiles={tiles}
      />
    </SummarySpacing>
  );
}
